import type { DataNode } from 'components/WorkspaceEditor/types/types'
import {
    analyzeNodeContentTypes,
    findNextArticleNode,
} from './contentAnalysis.utils'

// Performance optimization: Constants for visual feedback
const VISUAL_FEEDBACK_CONFIG = {
    OUTLINE_WIDTH: '1px',
    OUTLINE_COLOR: '#4A90E2',
    TRANSITION_DURATION: '0.3s',
    HIGHLIGHT_DURATION: 100,
    BORDER_RADIUS: '4px',
} as const

/**
 * Interface for tree reference used in virtualized scrolling
 */
export interface TreeRef {
    current?: {
        scrollTo?: (options: {
            key: string | number
            align?: 'top' | 'bottom' | 'auto'
            offset?: number
        }) => void
    }
}

/**
 * Interface for node expansion functionality
 */
export interface NodeExpansionContext {
    expandedKeys: string[]
    handleExpandCollapseArticle: (
        expandedKeys: string[],
        shouldExpand: boolean,
        node: DataNode
    ) => void
    findNodeByKey: (key: string) => DataNode | undefined
}

/**
 * Scrolls to a node in a virtualized tree and attempts to focus it
 *
 * @param treeRef - Reference to the scrollable tree component
 * @param nodeKey - The key of the node to scroll to
 * @param contentItemId - The ID of the content item to focus after scroll
 * @param withVisualFeedback - Whether to show visual feedback
 */
export const scrollToNodeInVirtualTree = (
    treeRef: TreeRef,
    nodeKey: string,
    contentItemId: string,
    withVisualFeedback: boolean = true,
    operation?: 'up' | 'down'
): void => {
    if (!treeRef.current?.scrollTo) return

    // Scroll the virtual tree to make the node visible
    treeRef.current.scrollTo({
        key: nodeKey,
        align: operation === 'up' ? 'bottom' : 'top',
        offset: 5,
    })

    // Wait for virtualization to render the element, then focus
    setTimeout(() => {
        focusTargetElement(contentItemId, withVisualFeedback)
    }, 150) // Slightly longer delay for virtualization
}

/**
 * Scrolls to an article node in a virtualized tree and attempts to focus its switcher icon
 *
 * @param treeRef - Reference to the scrollable tree component
 * @param nodeKey - The key of the article node to scroll to
 * @param withVisualFeedback - Whether to show visual feedback
 * @param operation - Navigation direction for virtual scrolling
 */
export const scrollToArticleInVirtualTree = (
    treeRef: TreeRef,
    nodeKey: string,
    withVisualFeedback: boolean = true,
    operation?: 'up' | 'down'
): void => {
    if (!treeRef.current?.scrollTo) return

    // Scroll the virtual tree to make the article node visible
    treeRef.current.scrollTo({
        key: nodeKey,
        align: operation === 'up' ? 'bottom' : 'top',
        offset: 5,
    })

    // Wait for virtualization to render the article element, then focus switcher
    setTimeout(() => {
        focusArticleExpandKey('', nodeKey, withVisualFeedback)
    }, 150) // Slightly longer delay for virtualization
}

/**
 * Focuses an article's expand/collapse switcher icon
 *
 * @param contentItemId - The ID of the content item (for compatibility, not used for articles)
 * @param nodeKey - The key of the article node
 * @param withVisualFeedback - Whether to show visual feedback
 * @param treeRef - Optional tree reference for virtualization support
 * @param operation - Navigation direction for virtual scrolling
 */
export const focusArticleExpandKey = (
    contentItemId: string,
    nodeKey: string,
    withVisualFeedback: boolean = true,
    treeRef?: TreeRef,
    operation?: 'up' | 'down'
): void => {
    try {
        const switcherIcon = document.querySelector(
            `[data-testid="tree-layout"] [data-testid="ant-tree"] [data-key="${nodeKey}"] [data-testid="switcher-icon"]`
        ) as HTMLElement

        // If element not found and we have tree ref, try virtual scroll
        if (!switcherIcon && treeRef && nodeKey) {
            scrollToArticleInVirtualTree(
                treeRef,
                nodeKey,
                withVisualFeedback,
                operation
            )
            return
        }

        if (!switcherIcon) {
            console.warn(`No switcher icon found for article node: ${nodeKey}`)
            return
        }

        switcherIcon.focus()

        if (withVisualFeedback) {
            const {
                OUTLINE_WIDTH,
                OUTLINE_COLOR,
                TRANSITION_DURATION,
                HIGHLIGHT_DURATION,
                BORDER_RADIUS,
            } = VISUAL_FEEDBACK_CONFIG

            switcherIcon.style.transition = `outline ${TRANSITION_DURATION} ease`
            switcherIcon.style.outline = `${OUTLINE_WIDTH} solid ${OUTLINE_COLOR}`
            switcherIcon.style.borderRadius = BORDER_RADIUS
            switcherIcon.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })

            setTimeout(() => {
                switcherIcon.style.outline = ''
                switcherIcon.style.borderRadius = ''
            }, HIGHLIGHT_DURATION)
        }
    } catch (error) {
        console.warn(`Error focusing switcher icon for node ${nodeKey}:`, error)
    }
}

/**
 * Checks if a node exists in a collapsed article and expands it if needed
 *
 * @param targetNodeKey - The key of the target node to check
 * @param expansionContext - Context containing expansion state and handlers
 * @returns Promise that resolves when expansion is complete (if needed)
 */
export const ensureNodeIsVisible = async (
    targetNodeKey: string,
    expansionContext?: NodeExpansionContext,
    treeData?: DataNode[]
): Promise<void> => {
    if (!expansionContext || !targetNodeKey) return

    const { expandedKeys, handleExpandCollapseArticle } = expansionContext

    // Check if target node is already visible in DOM
    const targetElement = document.querySelector(
        `[data-key="${targetNodeKey}"]`
    )
    if (targetElement) return // Node is already visible

    // Find the parent article node by traversing the tree structure
    const findParentArticleNode = (
        nodes: DataNode[],
        targetKey: string
    ): DataNode | null => {
        for (const node of nodes) {
            // If this is an article and contains our target, it's the parent
            if (node.type === 'Article') {
                if (node.key === targetKey) {
                    // Target is the article itself
                    return null
                }
                // Check if target is a descendant of this article
                const hasTargetDescendant = (children: DataNode[]): boolean => {
                    if (!children) return false
                    for (const child of children) {
                        if (child.key === targetKey) return true
                        if (
                            child.children &&
                            hasTargetDescendant(child.children)
                        )
                            return true
                    }
                    return false
                }
                if (hasTargetDescendant(node.children || [])) {
                    return node
                }
            }
            // Continue searching in children
            if (node.children) {
                const found = findParentArticleNode(node.children, targetKey)
                if (found) return found
            }
        }
        return null
    }

    // We need access to treeData to find parent article
    // For now, let's pass it through the SwitcherIcon context
    // This is a temporary solution - ideally we'd improve the NodeExpansionContext interface
    const treeDataToSearch = treeData || []
    const parentArticleNode = findParentArticleNode(
        treeDataToSearch,
        targetNodeKey
    )

    // If we found a parent article and it's not expanded, expand it
    if (parentArticleNode && !expandedKeys.includes(parentArticleNode.key)) {
        // Expand the article
        handleExpandCollapseArticle(
            [...expandedKeys, parentArticleNode.key],
            true,
            parentArticleNode
        )

        // Wait for expansion to complete and DOM to update
        await new Promise((resolve) => {
            // Use longer timeout to ensure virtualization and DOM updates complete
            setTimeout(resolve, 300)
        })
    }
}

/**
 * Focuses either an article's expand key or a content element based on node type
 * Now includes auto-expansion for collapsed articles
 *
 * @param contentItemId - The ID of the content item to focus
 * @param nodeKey - The node key for article targeting
 * @param isArticle - Whether this is an article node (focus expand key) or content node
 * @param withVisualFeedback - Whether to show visual feedback
 * @param treeRef - Optional tree reference for virtualization support
 * @param operation - Navigation direction for virtual scrolling
 * @param expansionContext - Optional context for auto-expanding collapsed articles
 */
export const focusNodeTarget = async (
    contentItemId: string,
    nodeKey: string | undefined,
    isArticle: boolean,
    withVisualFeedback: boolean = true,
    treeRef?: TreeRef,
    operation?: 'up' | 'down',
    expansionContext?: NodeExpansionContext,
    treeData?: DataNode[]
): Promise<void> => {
    // First, ensure the target node is visible (expand parent article if needed)
    if (nodeKey && expansionContext) {
        await ensureNodeIsVisible(nodeKey, expansionContext, treeData)
    }

    // Then focus the target as before
    if (isArticle && nodeKey) {
        focusArticleExpandKey(
            contentItemId,
            nodeKey,
            withVisualFeedback,
            treeRef,
            operation
        )
    } else {
        focusTargetElement(
            contentItemId,
            withVisualFeedback,
            treeRef,
            nodeKey,
            operation
        )
    }
}

/**
 * Focuses a target element and applies optional visual feedback
 *
 * @param contentItemId - The ID of the content item to focus
 * @param withVisualFeedback - Whether to show visual feedback
 * @param treeRef - Optional tree reference for virtualization support
 * @param nodeKey - Optional node key for virtual tree scrolling
 */
export const focusTargetElement = (
    contentItemId: string,
    withVisualFeedback: boolean = true,
    treeRef?: TreeRef,
    nodeKey?: string,
    operation?: 'up' | 'down'
): void => {
    try {
        const targetElement = document.querySelector(
            `[data-testid="plain-text-${contentItemId}"]`
        ) as HTMLElement

        // If element not found and we have tree ref, try virtual scroll
        if (!targetElement && treeRef && nodeKey) {
            scrollToNodeInVirtualTree(
                treeRef,
                nodeKey,
                contentItemId,
                withVisualFeedback,
                operation
            )
            return
        }

        if (!targetElement) {
            console.warn(`No content element found for ID: ${contentItemId}`)
            return
        }

        targetElement.focus()

        if (withVisualFeedback) {
            const {
                OUTLINE_WIDTH,
                OUTLINE_COLOR,
                TRANSITION_DURATION,
                HIGHLIGHT_DURATION,
                BORDER_RADIUS,
            } = VISUAL_FEEDBACK_CONFIG

            targetElement.style.transition = `outline ${TRANSITION_DURATION} ease`
            targetElement.style.outline = `${OUTLINE_WIDTH} solid ${OUTLINE_COLOR}`
            targetElement.style.borderRadius = BORDER_RADIUS
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })

            setTimeout(() => {
                targetElement.style.outline = ''
                targetElement.style.borderRadius = ''
            }, HIGHLIGHT_DURATION)
        }
    } catch (error) {
        console.warn(`Error focusing element ${contentItemId}:`, error)
    }
}

/**
 * Helper function to handle arrow navigation logic for both up and down directions
 * Reduces code duplication between arrow up and arrow down handlers
 *
 * @param direction - Navigation direction ('up' or 'down')
 * @param currentIndex - Current index in the visible nodes array
 * @param currentContent - The current node content
 * @param visibleNodes - Array of visible nodes
 * @param NODE_TYPES - Node type constants for analysis
 * @returns Navigation result object with target node and operation, or null if navigation is invalid
 */
export const handleArrowNavigation = (
    direction: 'up' | 'down',
    currentIndex: number,
    currentContent: DataNode,
    visibleNodes: DataNode[],
    NODE_TYPES: any
): {
    node: DataNode
    currentIndex: number
    operation: 'previous' | 'next'
} | null => {
    const isUp = direction === 'up'
    const delta = isUp ? -1 : 1
    const operation = isUp ? 'previous' : 'next'

    // Boundary check
    const newIndex = currentIndex + delta
    if (newIndex < 0 || newIndex >= visibleNodes.length) {
        return null
    }

    let targetNode = visibleNodes[newIndex]

    // Analyze current and target node content types using extracted utility
    const currentAnalysis = analyzeNodeContentTypes(currentContent, NODE_TYPES)
    const targetAnalysis = analyzeNodeContentTypes(targetNode, NODE_TYPES)

    // Only skip UOM/OptionGroup if they don't have focusable text content
    if (
        currentAnalysis.isUOMOrOptionGroup &&
        !targetAnalysis.hasFocusableText
    ) {
        // Look for the next/previous Article node using extracted utility
        const articleNode = findNextArticleNode(
            visibleNodes,
            currentIndex,
            operation,
            NODE_TYPES
        )
        if (articleNode) {
            targetNode = articleNode
        }
    }

    return {
        node: targetNode,
        currentIndex,
        operation: operation as 'previous' | 'next',
    }
}
